import React from "react";
import { useMutation } from "@apollo/client";
import {
  REMOVE_ASSESSMENT_OPTION,
  REMOVE_MISSION_ROLE_ATTACHMENT,
  UPDATE_MISSION_ROLE_CHALLENGE,
  UPDATE_MISSION_ROLE_CODE_CHALLENGES,
  UPDATE_MISSION_ROLE_REQUIREMENTS,
  UPDATE_MISSION_ROLE,
} from "graphql/mutations";
import { GET_MISSION_ROLE, GET_PUBLIC_MISSION_ROLE } from "graphql/queries";
import { useQueryData, useToast } from "hooks";
import { FormattedMessage } from "react-intl";
import { ICON_TYPE } from "components/Icon";

export default function useMissionRole({
  id,
  isPublicRole,
  skip,
  includeCustomerBudget = false,
  includeAttachments = false,
  includeDisabledChallenges = false,
  refetchQueries = [],
} = {}) {
  const { toast } = useToast();
  const keyName = isPublicRole ? "publicMissionRole" : "missionRole";
  const {
    loading,
    data: { [keyName]: missionRole } = {},
    error,
    refetch,
  } = useQueryData({
    queryName: isPublicRole ? GET_PUBLIC_MISSION_ROLE : GET_MISSION_ROLE,
    keyName: keyName,
    skip: !id || skip,
    variables: { slug: id, includeCustomerBudget, includeAttachments, includeDisabledChallenges },
  });

  const [updateMissionRole, { loading: updatingRole }] = useMutation(UPDATE_MISSION_ROLE);
  const [updateMissionRoleCodeChallenges, { loading: updating }] = useMutation(UPDATE_MISSION_ROLE_CODE_CHALLENGES);
  const [removeMissionRoleAttachment, { loading: attachmentRemoving }] = useMutation(REMOVE_MISSION_ROLE_ATTACHMENT);
  const [updateMissionRoleRequirements, { loading: updatingRequirements }] = useMutation(
    UPDATE_MISSION_ROLE_REQUIREMENTS
  );
  const [updateMissionRoleChallenge, { loading: updatingMissionRoleChallenge }] =
    useMutation(UPDATE_MISSION_ROLE_CHALLENGE);
  const [removeRequirement, { loading: removingRequirement }] = useMutation(REMOVE_ASSESSMENT_OPTION);

  /**
   * Triggers the remove attachment from mission role mutation
   */
  const handleRemoveAttachment = async (attachmentId) =>
    await removeMissionRoleAttachment({
      variables: { input: { id, attachmentId } },
      onCompleted: () =>
        toast.success(<FormattedMessage id="Missions.Roles.RoleAttachments.AdvancedOptions.Remove.Success" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleAttachments.AdvancedOptions.Remove.Error" />),
    });

  const handleUpdateMissionRoleCodeChallenges = ({ refetchQueries = [], ...input }) => {
    updateMissionRoleCodeChallenges({
      variables: {
        input,
        includeDisabledChallenges,
      },
      refetchQueries,
      onCompleted: () => toast.success(<FormattedMessage id="useMissionRole.UpdateSuccess" />),
      onError: ({ message }) => toast.error(message || <FormattedMessage id="useMissionRole.UpdateError" />),
    });
  };

  /**
   * Triggers the update requirements mutation
   */
  const handleUpdateRequirements = (requirements) =>
    updateMissionRoleRequirements({
      variables: { input: { id, params: { requirements } } },
      refetchQueries: [{ query: GET_MISSION_ROLE, variables: { slug: id, includeCustomerBudget, includeAttachments } }],
      onCompleted: () => toast.success(<FormattedMessage id="RoleRequirements.UpdateSuccess" />),
      onError: () => toast.error(<FormattedMessage id="RoleRequirements.UpdateError" />),
    });

  /**
   * Deletes a requirement outright, rather than only switching it off
   */
  const handleRemoveRequirement = (requirementId) =>
    removeRequirement({
      variables: { input: { id: requirementId } },
      refetchQueries: [GET_MISSION_ROLE, ...refetchQueries],
      onCompleted: () => toast.success(<FormattedMessage id="RoleRequirements.Remove.Success" />),
      onError: () => toast.error(<FormattedMessage id="RoleRequirements.Remove.Error" />),
    });

  const getRequirementRowOptions = (requirement) => [
    {
      label: <FormattedMessage id="Global.Remove" />,
      icon: ICON_TYPE.trash,
      disabled: removingRequirement,
      onClick: () => handleRemoveRequirement(requirement.id),
    },
  ];

  const handleToggleMissionRoleChallenge = ({ id, enabled }) => {
    updateMissionRoleChallenge({
      variables: {
        input: {
          id,
          params: {
            enabled,
          },
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useMissionRole.UpdateSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useMissionRole.UpdateError" />),
    });
  };

  const handleUpdateMissionRole = ({ id, params }) => {
    updateMissionRole({
      variables: {
        input: {
          id,
          params,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useMissionRole.UpdateSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useMissionRole.UpdateError" />),
    });
  };

  return {
    loading,
    refetch,
    data: missionRole,
    handleUpdateMissionRoleCodeChallenges,
    updating,
    handleUpdateMissionRole,
    updatingRole,
    error,
    handleRemoveAttachment,
    attachmentRemoving,
    handleUpdateRequirements,
    updatingRequirements,
    handleRemoveRequirement,
    removingRequirement,
    getRequirementRowOptions,
    handleToggleMissionRoleChallenge,
    updatingMissionRoleChallenge,
  };
}
